// app/components/MiniCartPanel.tsx
"use client";

import React, { useEffect, useState, useTransition } from "react";
import { XMarkIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  retrieveCart,
  updateLineItem,
  deleteLineItem,
  clearCart,
} from "@/app/actions/cart";
// comment for shiprocket checkout integration
// import PurchaseModalPopup from "@/app/components/PurchaseModalPopup";
import AuthModal from "./AuthModal";
import { toast } from "react-toastify";
import { useMiniCart } from "../context/MiniCartContext";
import { paymentInitiatedEvent } from "../utils/gtm-events/contact";

type ServerCart = {
  id: number;
  attributes: {
    currency?: string;
    currencySymbol?: string;
    totalAmount?: number;
    totalItems?: number;
    lineItems?: { data: any[] };
    cartSessionId?: string;
  };
};

export default function ShiprocketMiniCart({ onClose }: { onClose(): void }) {
  const [serverCart, setServerCart] = useState<ServerCart | null>(null);
  const [isPending, startTransition] = useTransition();
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  // comment for shiprocket checkout integration
  // const [isPurchaseOpen, setIsPurchaseOpen] = useState(false);

  const [authChecked, setAuthChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [cartLoading, setCartLoading] = useState(true);
  const { refreshCartTrigger } = useMiniCart();
  const { close } = useMiniCart();
  const loadCart = async () => {
    setCartLoading(true);
    const res = await retrieveCart();
    // console.log("response", res);
    setServerCart(res?.cart ?? res ?? null);
    setCartLoading(false);
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    loadCart();
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [refreshCartTrigger]);

  useEffect(() => {
    fetch("/api/me", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setIsLoggedIn(!!data.isLoggedIn))
      .catch(() => setIsLoggedIn(false))
      .finally(() => setAuthChecked(true));
  }, []);

  // ---------------------------
  // 1. Add this useEffect at top
  // ---------------------------
  useEffect(() => {
    // Add hidden sellerDomain input
    if (!document.getElementById("sellerDomain")) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.id = "sellerDomain";
      input.value = window.location.hostname; // domain
      //   console.log("Input Value......", input.value);
      document.body.appendChild(input);
    }

    // Load Shiprocket script once
    if (!(window as any).HeadlessCheckout) {
      const script = document.createElement("script");
      script.src =
        "https://checkout-ui.shiprocket.com/assets/js/channels/shopify.js";
      script.onload = () => console.log(" Shiprocket ");
      document.body.appendChild(script);
    }
  }, []);

  const lineItems = serverCart?.attributes?.lineItems?.data ?? [];
  const currencySymbol = serverCart?.attributes?.currencySymbol ?? "₹";
  const currency = serverCart?.attributes?.currency || "INR";
  const subtotal = serverCart?.attributes?.totalAmount ?? 0;

  const fmt = (v: number) => Intl.NumberFormat("en-IN").format(v);

  // async function handleShiprocketCheckout(e?: any) {
  //   e?.preventDefault?.();

  //   try {
  //     const cartData = {
  //       cart_data: {
  //         items: lineItems.map((item: any) => ({
  //           variant_id:
  //             item.attributes.variant?.data?.id ||
  //             item.attributes.product?.data?.id,
  //           quantity: item.attributes.quantity,
  //         })),
  //       },
  //       redirect_url: `${window.location.origin}/thankyou`,

  //       // redirect_url: `${window.location.origin}/checkout/success`,
  //       timestamp: new Date().toISOString(),
  //     };

  //     const res = await fetch("/api/shiprocket/checkout", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(cartData),
  //     });

  //     const data = await res.json();
  //     const token = data?.result?.token;
  //     if (!token) throw new Error("Failed to generate checkout token");

  //     // Add sellerDomain input if not already present
  //     if (!document.getElementById("sellerDomain")) {
  //       const input = document.createElement("input");
  //       input.type = "hidden";
  //       input.id = "sellerDomain";
  //       input.value = window.location.hostname; // your domain
  //       document.body.appendChild(input);
  //     }

  //     // Load script only once
  //     if (!(window as any).HeadlessCheckout) {
  //       await new Promise((resolve) => {
  //         const script = document.createElement("script");
  //         script.src =
  //           "https://checkout-ui.shiprocket.com/assets/js/channels/shopify.js";
  //         script.onload = resolve;
  //         document.body.appendChild(script);
  //       });
  //     }

  //     (window as any).HeadlessCheckout.addToCart(e, token, {
  //       fallbackUrl: `${window.location.origin}/thankyou?status=failure`,
  //     });
  //   } catch (err: any) {
  //     console.error(err);
  //     toast.error(err.message || "Checkout failed, please try again.");
  //   }
  // }

  //comment for shiprocket checkout integration
  // const handleCheckoutClick = () => {
  //   if (!authChecked) return; // still checking auth
  //   if (isLoggedIn) {
  //     setIsPurchaseOpen(true); // ✅ directly open checkout modal
  //   } else {
  //     setShowAuthModal(true); // ❌ open login modal
  //   }
  // };

  // ---------------------------
  // 2. Update handleShiprocketCheckout
  // ---------------------------
  async function handleShiprocketCheckout(e?: any) {
    e?.preventDefault?.();

    // console.log("handleShiprocketCheckout called event", e);
    try {
      const cartData = {
        cart_data: {
          items: lineItems.map((item: any) => ({
            variant_id:
              item.attributes.variant?.data?.id ||
              item.attributes.product?.data?.id,
            quantity: item.attributes.quantity,
          })),
        },
        redirect_url: `${window.location.origin}/thankyou`,
        timestamp: new Date().toISOString(),
      };

      const finalAmount = serverCart?.attributes?.totalAmount || 0;

      paymentInitiatedEvent({
        pageLocation: window.location.pathname,
        amount: finalAmount,
        currency: "INR",
        paymentMethod: "shiprocket",
      });
      //   console.log("Cart Data Payload >>>", JSON.stringify(cartData, null, 2));

      const res = await fetch("/api/shiprocket/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cartData),
      });

      const data = await res.json();
      const token = data?.result?.token;
      if (!token) throw new Error("Failed to generate checkout token");

      // ✅ Script already loaded in useEffect
      if ((window as any).HeadlessCheckout) {
        const nativeEvent = e?.nativeEvent || e;
        // console.log("Native Event...", nativeEvent);
        // console.log("Token...", token);
        (window as any).HeadlessCheckout.addToCart(nativeEvent, token, {
          fallbackUrl: `${window.location.origin}/thankyou?status=failure`,
          //   successCallback: async () => {
          //     console.log("✅ Checkout successful! Clearing cart...");
          //     await clearCart(); // Cart ko clear karein
          //     await loadCart(); // Mini cart panel ko update karne ke liye cart dobara load karein (jo ab khaali hoga)
          //     close(); // Mini cart panel ko band kar dein
          //     console.log("🛒 Cart cleared and UI updated.");
          //   },
        });
      } else {
        throw new Error("Shiprocket checkout script not loaded");
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Checkout failed, please try again.");
    }
  }

  const handleCheckoutClick = (e: any) => {
    if (!authChecked) return;
    if (isLoggedIn) {
      handleShiprocketCheckout(e); // ✅ Shiprocket flow
    } else {
      setShowAuthModal(true);
    }
  };

  const handleQtyUpdate = (lineId: number, qty: number) => {
    if (qty < 1) return;
    startTransition(async () => {
      setLoadingId(lineId);
      await updateLineItem({ lineId, quantity: qty });
      await loadCart();
      setLoadingId(null);
    });
  };

  const handleDelete = (lineId: number) => {
    startTransition(async () => {
      setLoadingId(lineId);
      await deleteLineItem(lineId);
      setConfirmDeleteId(null);
      await loadCart();
      setLoadingId(null);
    });
  };

  // Helper function to get variant size with fallback
  const getVariantSize = (item: any) => {
    const variantTitle = item.attributes.variant?.data?.attributes?.title;
    if (!variantTitle) return null;

    // Clean up the variant title - remove any extra text and keep just the size
    const sizeMatch = variantTitle.match(
      /(\d+(?:\.\d+)?\s*(?:kg|g|ml|l|oz|lb)?)/i
    );
    return sizeMatch ? sizeMatch[1].trim() : variantTitle;
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={() => {
          // comment for shiprocket checkout integration
          // if (!isPurchaseOpen)
          onClose();
        }}
      />

      <aside className="relative ml-auto w-full max-w-[460px] bg-white shadow-xl rounded-l-2xl overflow-hidden">
        {(loadingId !== null || isPending) && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-50">
            <svg
              className="animate-spin h-8 w-8 text-[var(--goenka-gold)]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
          </div>
        )}

        <header className="flex items-center justify-between p-4 border-b border-[var(--shadow)]">
          <h2 className="text-lg font-semibold text-[var(--title-color)]">
            Shopping Cart ({lineItems.length})
          </h2>
          <button
            onClick={() => {
              // comment for shiprocket checkout integration
              // if (!isPurchaseOpen)

              onClose();
            }}
            className="rounded-full hover:rotate-180 transition-transform duration-300"
          >
            <XMarkIcon className="h-6 w-6 text-[var(--text-2)] hover:text-[var(--text-1)]" />
          </button>
        </header>

        <div className="p-4 overflow-y-auto h-[calc(100vh-200px)] space-y-4">
          {cartLoading ? (
            <div className="flex justify-center items-center py-16">
              <p>Loading your cart...</p>
            </div>
          ) : lineItems.length === 0 ? (
            <div className="text-center flex flex-col items-center justify-center py-16">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-24 h-24 mb-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 9h12l-2-9M10 21a1 1 0 100-2 1 1 0 000 2zm7 0a1 1 0 100-2 1 1 0 000 2z"
                />
              </svg>
              <p className="text-lg font-semibold">Your cart is empty</p>
              <p className="text-sm text-gray-500 mb-4">
                You have no items in your shopping cart.
              </p>
              {/* <button
                    onClick={onClose}
                    className="bg-[#002244] text-white px-6 py-2 rounded-md font-semibold"
                >
                    CONTINUE SHOPPING
                </button> */}
            </div>
          ) : (
            lineItems.map((item: any) => {
              const attr = item.attributes;
              const product = attr.product?.data?.attributes;
              const imgSrc =
                product?.wb_product_images?.data?.[0]?.attributes?.src ||
                product?.images?.data?.[0]?.attributes?.url;
              const title = product?.Title ?? "Product";
              const quantity = attr.quantity ?? 1;
              const unitPrice = attr.unitPrice ?? 0;
              const totalPrice = unitPrice * quantity;
              const variantSize = getVariantSize(item);
              const showDelete = confirmDeleteId === item.id;

              return (
                <>
                  <div
                    key={item.id}
                    className={`relative flex items-center gap-4 bg-[var(--bg-col-1)] p-3 rounded-lg shadow-sm transition-all duration-300 ease-in-out ${
                      showDelete ? "bg-red-50" : ""
                    }`}
                  >
                    <img
                      src={imgSrc}
                      alt={title}
                      className="h-20 w-28 object-cover rounded-md"
                    />

                    {!showDelete ? (
                      <div className="flex-1 transition-all">
                        <h3 className="text-[var(--text-3)] font-medium text-sm line-clamp-2 mb-1">
                          {title}
                        </h3>

                        {/* Variant Size Display */}
                        {variantSize && (
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-gray-500 font-medium">
                              Size:
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-medium border border-gray-200">
                              {variantSize}
                            </span>
                          </div>
                        )}

                        <p className="mb-2 font-semibold text-[var(--goenka-gold)] text-sm">
                          {currencySymbol} {fmt(totalPrice)}
                          {quantity > 1 && (
                            <span className="text-xs text-gray-500 font-normal ml-1">
                              ({currencySymbol} {fmt(unitPrice)} each)
                            </span>
                          )}
                        </p>

                        <div className="flex items-center justify-between">
                          <div className="inline-flex items-center justify-center border border-gray-300 rounded overflow-hidden text-sm">
                            <button
                              onClick={() =>
                                handleQtyUpdate(item.id, quantity - 1)
                              }
                              disabled={loadingId === item.id || quantity <= 1}
                              className="px-2 py-[5px] hover:bg-gray-200 disabled:opacity-50 transition-colors"
                            >
                              −
                            </button>
                            <span className="px-3 py-[5px] text-[var(--text-2)] min-w-[40px] text-center">
                              {quantity}
                            </span>
                            <button
                              onClick={() =>
                                handleQtyUpdate(item.id, quantity + 1)
                              }
                              disabled={loadingId === item.id}
                              className="px-2 py-[5px] hover:bg-gray-200 transition-colors"
                            >
                              +
                            </button>
                          </div>

                          <button
                            onClick={() => setConfirmDeleteId(item.id)}
                            className="p-1.5 text-white bg-red-600 hover:bg-red-700 rounded-full transition-colors"
                            title="Remove item"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 animate-slide-in-left">
                        <p className="text-sm font-semibold mb-2">
                          Are you sure you want to remove?
                        </p>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="bg-red-600 text-white px-4 py-1 rounded-full text-sm hover:bg-red-700 transition-colors"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="border border-gray-300 text-gray-700 px-4 py-1 rounded-full text-sm hover:bg-gray-50 transition-colors"
                          >
                            No
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              );
            })
          )}
          {/* Centered Continue Shopping */}
          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="bg-[#b12b90] text-white px-6 py-2 rounded-md font-semibold mt-5"
            >
              CONTINUE SHOPPING
            </button>
          </div>
        </div>

        {lineItems.length > 0 && (
          <footer className="p-4 border-t border-[var(--shadow)] space-y-4">
            <div className="flex justify-between font-semibold text-[var(--text-3)] text-lg">
              <span>Subtotal</span>
              <span>
                {currencySymbol} {fmt(subtotal ?? 0)}
              </span>
            </div>
            <button
              // comment for shiprocket checkout integration
              // onClick={handleCheckoutClick}
              onClick={(e) => handleCheckoutClick(e)}
              disabled={!authChecked}
              className="w-full py-3 rounded-lg font-medium bg-[#b12b90] text-white hover:bg-[#e605ae] transition-colors"
            >
              {authChecked ? "Checkout" : "Checking…"}
            </button>
            {/* <button
                onClick={() => setIsPurchaseOpen(true)}
                disabled={!authChecked}
                
                className="w-full py-3 rounded-lg font-medium bg-neutral-900 text-white hover:bg-neutral-800 transition-colors"
                >
                {authChecked ? "Checkout" : "Checking…"}
                </button> */}
          </footer>
        )}
      </aside>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={async () => {
          setShowAuthModal(false);

          // ✅ Reload cart after login
          await loadCart();
          // comment for shiprocket checkout integration
          // ✅ Open checkout modal
          // setIsPurchaseOpen(true);

          handleShiprocketCheckout();
        }}
      />
      {/* //comment for shiprocket checkout integration */}
      {/* <PurchaseModalPopup
            isOpen={isPurchaseOpen}
            onClose={() => setIsPurchaseOpen(false)}
            product={{ price: subtotal, currency, currencySymbol }}
            orderItems={lineItems}
            cartSessionId={serverCart?.attributes?.cartSessionId}
            preOrder={false}
            subtotal={subtotal}
        /> */}
    </div>
  );
}
